import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import NodePalette from '../components/journey/NodePalette';
import JourneyCanvas from '../components/journey/JourneyCanvas';
import ConfigDrawer from '../components/journey/ConfigDrawer';
import NodeReportPanel from '../components/journey/NodeReportPanel';
import Select from '../components/ui/Select';
import { defaultCfg, NODE_DEFS } from '../data/nodeDefinitions';
import EventBus from '../utils/EventBus';
import { journeyService } from '../services/journeyService';
import { segmentService } from '../services/segmentService';
import { nodesToJourneyConfig, graphFromConfig } from '../utils/journeyConfig';
import { buildOverlay } from '../utils/journeyOverlay';
import { formatCompactNumber, formatRelativeTime } from '../utils/format';
import styles from './JourneyBuilder.module.css';

const DATE_RANGES = [
  { value: 'all', label: 'All time' },
  { value: '7d', label: 'Last 7 days' },
  { value: '30d', label: 'Last 30 days' },
];

let _nodeId = 1;
const uid = (p) => p + Date.now().toString(36) + '-' + Math.floor(Math.random() * 1e6).toString(36);

class JourneyBuilder extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      journeyId: props.journeyId !== 'new' ? props.journeyId : null,
      name: 'New Journey',
      status: 'draft',
      mode: 'build',            // 'build' (edit draft) | 'live' (view published)
      nodes: [],
      edges: [],
      selectedId: null,         // single node whose config drawer is open
      selectedIds: [],          // multi-selection (for duplicate/group ops)
      drawerOpen: false,
      liveGraph: null,
      liveVersion: null,
      stats: null,
      segmentNames: {},          // segment id -> name, for Entry node display
      saving: false,
      loading: props.journeyId !== 'new',
      initialViewport: null,
      canUndo: false,
      canRedo: false,
      // Live View reporting
      report: null,
      versions: [],
      reportVersion: '',        // '' = all versions
      dateRange: 'all',
      liveNodeId: null,         // node whose drill-down report panel is open
    };
    this._viewport = null;
    // Undo/redo — snapshots of {nodes, edges}. commit() is called BEFORE a
    // mutation (captures pre-change state); rapid successive commits within a
    // short window coalesce so a multi-node delete / group drag is one undo step.
    this._past = [];
    this._future = [];
    this._lastCommitAt = 0;
    this._cfgDirty = false;

    [
      'addNode', 'moveNode', 'deleteNode', 'addEdge', 'deleteEdge', 'selectNode',
      'updateCfg', 'saveDraft', 'publish', 'onViewport', 'undo', 'redo',
      'onSelectionChange', 'onInteractStart', 'duplicateSelected', 'addNodeConnected',
      'insertNodeOnEdge', 'onKey', 'fetchReport', 'onLiveNodeClick',
    ].forEach((m) => { this[m] = this[m].bind(this); });
  }

  componentDidMount() {
    window.addEventListener('keydown', this.onKey);
    // Resolve segment names for Entry node display (config stores only the id).
    segmentService.getAll()
      .then((segs) => this.setState({ segmentNames: Object.fromEntries((segs || []).map((s) => [s.id, s.name])) }))
      .catch(() => {});
    if (!this.state.journeyId) return;
    journeyService.getById(this.state.journeyId)
      .then((journey) => {
        const draft = journey.draft_config || journey.config;
        const build = graphFromConfig(draft);
        // Draft ui is the source of truth for layout — kept so Live View can
        // borrow node positions when a published version predates position
        // saving (positions are cosmetic; structure still comes from the
        // published/selected config).
        this._draftUi = draft?.ui && draft.ui.nodes?.length ? draft.ui : null;
        const live = journey.live_version_id ? this._liveGraph(journey.config) : null;
        this._syncNodeIdCounter(build.nodes);
        this.setState({
          loading: false, name: journey.name, status: journey.status,
          nodes: build.nodes, edges: build.edges,
          initialViewport: build.viewport || null, liveGraph: live,
        });
        this._viewport = build.viewport || null;
        this._resolveSegmentNames([...build.nodes, ...(live?.nodes || [])]);
      })
      .catch(() => {
        this.setState({ loading: false });
        EventBus.emit('toast', { type: 'error', message: 'Failed to load journey.' });
      });
    this.loadStats();
  }

  // Resolve names for any segment ids referenced by entry nodes — the bulk
  // segments list excludes auto-generated/ad-hoc segments and is paginated, so
  // fetch each referenced segment by id directly (covers deleted-from-list and
  // never-named-in-config cases). Merges into segmentNames for Entry display.
  _resolveSegmentNames(nodes) {
    const ids = new Set();
    (nodes || []).forEach((n) => {
      const isEntry = n.type === 'entry' || n.type === 'entry-segment' || n.type === 'entry-schedule';
      if (isEntry && n.cfg?.segmentId) ids.add(n.cfg.segmentId);
    });
    ids.forEach((id) => {
      if (this.state.segmentNames[id]) return;
      segmentService.getById(id)
        .then((seg) => { if (seg?.name) this.setState((s) => ({ segmentNames: { ...s.segmentNames, [id]: seg.name } })); })
        .catch(() => {});
    });
  }

  // Live View graph for a given (published/version) config. Prefers that config's
  // own saved ui positions; if it has none (published before positions were
  // saved, or a backfilled v1), borrows the draft's layout so Live View matches
  // what was built instead of dropping to a scattered auto-layout grid.
  _liveGraph(config) {
    if (config?.ui?.nodes?.length) return graphFromConfig(config);
    if (this._draftUi) return graphFromConfig({ ...config, ui: this._draftUi });
    return graphFromConfig(config);
  }

  componentWillUnmount() {
    window.removeEventListener('keydown', this.onKey);
  }

  // Keep the module-level id counter ahead of any ids restored from a saved
  // graph, so freshly-added nodes never collide with loaded ones.
  _syncNodeIdCounter(nodes) {
    nodes.forEach((n) => {
      const m = /^n(\d+)$/.exec(n.id);
      if (m) _nodeId = Math.max(_nodeId, Number(m[1]) + 1);
    });
  }

  loadStats() {
    if (!this.state.journeyId) return;
    journeyService.stats(this.state.journeyId).then((stats) => this.setState({ stats })).catch(() => {});
  }

  onViewport(vp) { this._viewport = vp; }

  // ---- history ----------------------------------------------------------
  _snapshot() {
    return {
      nodes: this.state.nodes.map((n) => ({ ...n, cfg: { ...n.cfg } })),
      edges: this.state.edges.map((e) => ({ ...e })),
    };
  }

  _commit() {
    const now = Date.now();
    // Coalesce a burst of programmatic changes (multi-remove, group drag) that
    // all fire within one gesture into a single undo step.
    if (this._lastCommitAt && now - this._lastCommitAt < 350) { this._lastCommitAt = now; return; }
    this._lastCommitAt = now;
    this._past.push(this._snapshot());
    if (this._past.length > 60) this._past.shift();
    this._future = [];
    if (!this.state.canUndo || this.state.canRedo) this.setState({ canUndo: true, canRedo: false });
  }

  onInteractStart() { this._commit(); }   // fired once at drag start by the canvas

  undo() {
    if (!this._past.length) return;
    this._future.push(this._snapshot());
    const prev = this._past.pop();
    this._lastCommitAt = 0;
    this._cfgDirty = false;
    this.setState({ nodes: prev.nodes, edges: prev.edges, selectedId: null, selectedIds: [], drawerOpen: false, canUndo: this._past.length > 0, canRedo: true });
  }

  redo() {
    if (!this._future.length) return;
    this._past.push(this._snapshot());
    const next = this._future.pop();
    this._lastCommitAt = 0;
    this._cfgDirty = false;
    this.setState({ nodes: next.nodes, edges: next.edges, selectedId: null, selectedIds: [], drawerOpen: false, canUndo: true, canRedo: this._future.length > 0 });
  }

  onKey(e) {
    if (this.state.mode !== 'build') return;
    const tag = (e.target.tagName || '').toUpperCase();
    if (tag === 'INPUT' || tag === 'TEXTAREA' || e.target.isContentEditable) return;
    const mod = e.metaKey || e.ctrlKey;
    if (!mod) return;
    const k = e.key.toLowerCase();
    if (k === 'z') { e.preventDefault(); e.shiftKey ? this.redo() : this.undo(); }
    else if (k === 'y') { e.preventDefault(); this.redo(); }
    else if (k === 'd') { e.preventDefault(); this.duplicateSelected(); }
  }

  // ---- mutations --------------------------------------------------------
  addNode(type, x, y) {
    this._commit();
    const id = 'n' + (_nodeId++);
    const px = x != null ? x : 60 + (this.state.nodes.length % 3) * 240;
    const py = y != null ? y : 60 + Math.floor(this.state.nodes.length / 3) * 160;
    this.setState((s) => ({ nodes: [...s.nodes, { id, type, x: px, y: py, cfg: defaultCfg(type) }] }));
  }

  // New node created + wired straight off an existing connector (quick-add).
  addNodeConnected(type, x, y, fromId, fromHandle) {
    this._commit();
    const id = 'n' + (_nodeId++);
    const handle = fromHandle || 'out';
    this.setState((s) => ({
      nodes: [...s.nodes, { id, type, x, y, cfg: defaultCfg(type) }],
      edges: [
        ...s.edges.filter((e) => !(e.from === fromId && (e.fromHandle || 'out') === handle)),
        { id: uid('e'), from: fromId, to: id, fromHandle: handle },
      ],
    }));
  }

  // Insert a node into an existing edge: from → new → to.
  insertNodeOnEdge(edgeId, type) {
    this._commit();
    this.setState((s) => {
      const edge = s.edges.find((e) => e.id === edgeId);
      if (!edge) return {};
      const from = s.nodes.find((n) => n.id === edge.from);
      const to = s.nodes.find((n) => n.id === edge.to);
      const x = from && to ? (from.x + to.x) / 2 : (from ? from.x : 60);
      const y = from && to ? (from.y + to.y) / 2 : (from ? from.y + 120 : 60);
      const id = 'n' + (_nodeId++);
      const outHandle = (NODE_DEFS[type]?.handles?.out || ['out'])[0];
      const edges = s.edges.filter((e) => e.id !== edgeId);
      edges.push({ id: uid('e'), from: edge.from, to: id, fromHandle: edge.fromHandle || 'out' });
      if (outHandle) edges.push({ id: uid('e'), from: id, to: edge.to, fromHandle: outHandle });
      return { nodes: [...s.nodes, { id, type, x, y, cfg: defaultCfg(type) }], edges };
    });
  }

  duplicateSelected() {
    const ids = this.state.selectedIds;
    if (!ids.length) return;
    this._commit();
    this.setState((s) => {
      const idMap = {};
      const dupes = [];
      s.nodes.forEach((n) => {
        // Never duplicate the entry node — a journey has exactly one.
        if (ids.includes(n.id) && n.type !== 'entry' && !n.type.startsWith('entry-')) {
          const nid = 'n' + (_nodeId++);
          idMap[n.id] = nid;
          dupes.push({ ...n, id: nid, x: n.x + 40, y: n.y + 40, cfg: { ...n.cfg } });
        }
      });
      const dupEdges = s.edges
        .filter((e) => idMap[e.from] && idMap[e.to])
        .map((e) => ({ id: uid('e'), from: idMap[e.from], to: idMap[e.to], fromHandle: e.fromHandle }));
      if (!dupes.length) return {};
      return { nodes: [...s.nodes, ...dupes], edges: [...s.edges, ...dupEdges], selectedIds: dupes.map((n) => n.id) };
    });
    EventBus.emit('toast', { type: 'success', message: 'Duplicated ' + ids.length + ' node(s).' });
  }

  moveNode(id, x, y) {
    // No commit here — the pre-drag state was captured by onInteractStart.
    this.setState((s) => ({ nodes: s.nodes.map((n) => (n.id === id ? { ...n, x, y } : n)) }));
  }

  deleteNode(id) {
    this._commit();
    this.setState((s) => ({
      nodes: s.nodes.filter((n) => n.id !== id),
      edges: s.edges.filter((e) => e.from !== id && e.to !== id),
      selectedId: s.selectedId === id ? null : s.selectedId,
      selectedIds: s.selectedIds.filter((x) => x !== id),
      drawerOpen: s.selectedId === id ? false : s.drawerOpen,
    }));
  }

  addEdge(fromId, fromHandle, toId) {
    if (fromId === toId) return;
    this._commit();
    const handle = fromHandle || 'out';
    this.setState((s) => ({
      edges: [
        ...s.edges.filter((e) => !(e.from === fromId && (e.fromHandle || 'out') === handle)),
        { id: uid('e'), from: fromId, to: toId, fromHandle: handle },
      ],
    }));
  }

  deleteEdge(id) {
    this._commit();
    this.setState((s) => ({ edges: s.edges.filter((e) => e.id !== id) }));
  }

  selectNode(id) {
    this._cfgDirty = false;
    this.setState({ selectedId: id, drawerOpen: true });
  }

  onSelectionChange(ids) {
    this.setState({ selectedIds: ids });
  }

  updateCfg(newCfg) {
    if (!this._cfgDirty) { this._commit(); this._cfgDirty = true; }  // one undo step per edit session
    const { selectedId } = this.state;
    this.setState((s) => ({ nodes: s.nodes.map((n) => (n.id === selectedId ? { ...n, cfg: newCfg } : n)) }));
  }

  // ---- persistence ------------------------------------------------------
  _persist() {
    const { journeyId, name, nodes, edges } = this.state;
    const { config, skipped } = nodesToJourneyConfig(nodes, edges, this._viewport);
    this._draftUi = config.ui; // keep the layout cache current for Live View fallback
    if (skipped.length) {
      EventBus.emit('toast', { type: 'info', message: `Skipped unsupported step type(s): ${skipped.join(', ')}` });
    }
    if (journeyId) {
      return journeyService.update(journeyId, { name, config }).then(() => journeyId);
    }
    return journeyService.create({ name, config }).then((journey) => {
      this.setState({ journeyId: journey.id });
      this.props.onCreated?.(journey.id);
      return journey.id;
    });
  }

  saveDraft() {
    this.setState({ saving: true });
    try {
      this._persist()
        .then(() => { this.setState({ saving: false }); EventBus.emit('toast', { type: 'success', message: 'Draft saved.' }); })
        .catch((err) => { this.setState({ saving: false }); EventBus.emit('toast', { type: 'error', message: err.message || 'Failed to save draft.' }); });
    } catch (err) {
      this.setState({ saving: false });
      EventBus.emit('toast', { type: 'error', message: err.message });
    }
  }

  publish() {
    if (!this.state.nodes.length) {
      EventBus.emit('toast', { type: 'error', message: 'Add at least one node before publishing.' });
      return;
    }
    this.setState({ saving: true });
    try {
      this._persist()
        .then((id) => journeyService.publish(id))
        .then((journey) => {
          this.setState((s) => ({
            saving: false, status: journey.status,
            liveVersion: journey.published_version || s.liveVersion,
            liveGraph: { nodes: s.nodes, edges: s.edges, viewport: this._viewport },
          }));
          this.loadStats();
          EventBus.emit('toast', { type: 'success', message: '"' + this.state.name + '" published! (v' + (journey.published_version || '') + ')' });
        })
        .catch((err) => { this.setState({ saving: false }); EventBus.emit('toast', { type: 'error', message: err.message || 'Failed to publish.' }); });
    } catch (err) {
      this.setState({ saving: false });
      EventBus.emit('toast', { type: 'error', message: err.message });
    }
  }

  setMode(mode) {
    if (mode === 'live' && !this.state.liveGraph) return;
    this.setState({ mode, selectedId: null, drawerOpen: false, liveNodeId: null }, () => {
      if (mode === 'live') { this.fetchVersions(); this.fetchReport(); this.loadStats(); }
    });
  }

  fetchVersions() {
    if (!this.state.journeyId || this.state.versions.length) return;
    journeyService.versions(this.state.journeyId).then((versions) => this.setState({ versions: versions || [] })).catch(() => {});
  }

  // Translates the date-range selector into API from/to params (ISO — the
  // backend converts to ClickHouse's format).
  _rangeParams() {
    if (this.state.dateRange === 'all') return {};
    const days = this.state.dateRange === '7d' ? 7 : 30;
    return { from: new Date(Date.now() - days * 86400e3).toISOString(), to: new Date().toISOString() };
  }

  fetchReport() {
    if (!this.state.journeyId) return;
    this.setState({ reportLoading: true });
    journeyService.report(this.state.journeyId, {
      version: this.state.reportVersion || undefined,
      ...this._rangeParams(),
    })
      .then((report) => this.setState({ report, reportLoading: false }))
      .catch(() => this.setState({ reportLoading: false }));
  }

  onLiveNodeClick(id) {
    this.setState({ liveNodeId: id });
  }

  render() {
    const {
      name, status, mode, nodes, edges, liveGraph, stats,
      selectedId, drawerOpen, saving, loading, initialViewport, canUndo, canRedo,
      report, versions, reportVersion, dateRange, liveNodeId,
    } = this.state;
    const selNode = nodes.find((n) => n.id === selectedId);
    const isLive = mode === 'live';
    const hasLive = !!liveGraph;

    if (loading) return <div className={styles.builder} />;

    // In Live View, render the graph of the selected version (or the live graph
    // for "all versions") so report node_steps line up with the nodes shown.
    const activeVersion = reportVersion ? versions.find((v) => v.id === reportVersion) : null;
    const liveViewGraph = activeVersion ? this._liveGraph(activeVersion.config) : liveGraph;
    const overlay = isLive ? buildOverlay(report, liveViewGraph?.nodes || [], liveViewGraph?.edges || []) : { metrics: {}, edgeLabels: {} };

    const viewNodes = isLive ? (liveViewGraph?.nodes || []) : nodes;
    const viewEdges = isLive ? (liveViewGraph?.edges || []) : edges;
    const viewViewport = isLive ? (liveViewGraph?.viewport || null) : initialViewport;
    const liveNode = isLive && liveNodeId ? (liveViewGraph?.nodes || []).find((n) => n.id === liveNodeId) : null;
    const lastEventAt = report?.freshness?.last_event_at;

    return (
      <div className={styles.builder}>
        <div className={styles.topbar}>
          <div className={styles.titleWrap}>
            <input
              className={styles.titleInput}
              value={name}
              onChange={(e) => this.setState({ name: e.target.value })}
              title="Click to rename"
              disabled={isLive}
            />
            <span className={[styles.statusPill, styles['s-' + status]].join(' ')}>
              {status.charAt(0).toUpperCase() + status.slice(1)}
            </span>
            <div className={styles.modeToggle}>
              <button className={[styles.modeBtn, !isLive ? styles.modeBtnOn : ''].join(' ')} onClick={() => this.setMode('build')}>
                <span className="material-symbols-rounded" style={{ fontSize: 15 }}>edit</span>
                Build
              </button>
              <button
                className={[styles.modeBtn, isLive ? styles.modeBtnOn : ''].join(' ')}
                onClick={() => this.setMode('live')}
                disabled={!hasLive}
                title={hasLive ? 'View the published journey' : 'Publish this journey to enable Live View'}
              >
                <span className="material-symbols-rounded" style={{ fontSize: 15 }}>bar_chart</span>
                Live View
              </button>
            </div>
          </div>
          {!isLive && (
            <div className={styles.tbRight}>
              <button className={styles.tbBtn} onClick={this.saveDraft} disabled={saving}>
                {saving ? 'Saving…' : 'Save Draft'}
              </button>
              <button className={[styles.tbBtn, styles.tbPri].join(' ')} onClick={this.publish} disabled={saving}>
                ▶ Publish
              </button>
            </div>
          )}
        </div>

        {isLive && (
          <div className={styles.liveBar}>
            <div className={styles.liveStat}><span className={styles.liveStatVal}>{stats ? formatCompactNumber(stats.users_in_journey) : '—'}</span><span className={styles.liveStatLbl}>Active users</span></div>
            <div className={styles.liveStat}><span className={styles.liveStatVal}>{report ? formatCompactNumber(report.totals.admitted) : '—'}</span><span className={styles.liveStatLbl}>Admitted</span></div>
            <div className={styles.liveStat}><span className={styles.liveStatVal}>{stats ? formatCompactNumber(stats.completed) : '—'}</span><span className={styles.liveStatLbl}>Completed flow</span></div>
            <div className={styles.liveStat}><span className={styles.liveStatVal}>{stats ? formatCompactNumber(stats.link_clicks) : '—'}</span><span className={styles.liveStatLbl}>Link clicks</span></div>

            <div className={styles.liveFilters}>
              <Select
                size="sm"
                style={{ width: 150 }}
                value={reportVersion}
                options={[{ value: '', label: 'All versions' }, ...versions.map((v) => ({ value: v.id, label: 'v' + v.version }))]}
                onChange={(val) => this.setState({ reportVersion: val, liveNodeId: null }, this.fetchReport)}
              />
              <Select
                size="sm"
                style={{ width: 130 }}
                value={dateRange}
                options={DATE_RANGES}
                onChange={(val) => this.setState({ dateRange: val }, this.fetchReport)}
              />
              <button className={styles.tbBtn} onClick={this.fetchReport} title="Refresh report">↻</button>
            </div>

            <span className={styles.liveVer}>
              {lastEventAt ? 'Updated ' + formatRelativeTime(lastEventAt) : 'No activity yet'}
              {' · counts = journey trips'}
            </span>
          </div>
        )}

        <div className={styles.canvasWrap}>
          {!isLive && <NodePalette onAdd={this.addNode} />}
          <JourneyCanvas
            key={mode + ':' + (reportVersion || 'all')}
            readOnly={isLive}
            nodes={viewNodes}
            edges={viewEdges}
            defaultViewport={viewViewport}
            onViewportChange={this.onViewport}
            selectedId={isLive ? null : selectedId}
            onSelect={isLive ? () => {} : this.selectNode}
            onSelectionChange={this.onSelectionChange}
            onInteractStart={this.onInteractStart}
            onMove={this.moveNode}
            onDelete={this.deleteNode}
            onDeselect={() => this.setState({ selectedId: null, drawerOpen: false })}
            onConnect={this.addEdge}
            onEdgeDelete={this.deleteEdge}
            onAddAt={this.addNode}
            onQuickAdd={this.addNodeConnected}
            onInsertEdge={this.insertNodeOnEdge}
            canUndo={canUndo}
            canRedo={canRedo}
            onUndo={this.undo}
            onRedo={this.redo}
            metricsByNodeId={isLive ? overlay.metrics : undefined}
            edgeLabels={isLive ? overlay.edgeLabels : undefined}
            onLiveNodeClick={this.onLiveNodeClick}
            segmentNames={this.state.segmentNames}
          />
        </div>

        <ConfigDrawer
          open={drawerOpen && !isLive}
          node={selNode}
          onClose={() => { this._cfgDirty = false; this.setState({ drawerOpen: false, selectedId: null }); }}
          onChange={this.updateCfg}
        />

        <NodeReportPanel
          open={isLive && !!liveNode}
          node={liveNode}
          metrics={liveNode ? overlay.metrics[liveNode.id] : null}
          onClose={() => this.setState({ liveNodeId: null })}
        />
      </div>
    );
  }
}

function JourneyBuilderWithRoute() {
  const { id } = useParams();
  const navigate = useNavigate();
  return (
    <JourneyBuilder
      journeyId={id}
      onCreated={(newId) => navigate('/journeys/' + newId + '/builder', { replace: true })}
    />
  );
}

export default JourneyBuilderWithRoute;
